/**
 * PostHog Analytics Provider (Eager Load)
 *
 * Simplified from the Nuxt 3 lazy-load pattern.
 * Bifrost is an app behind auth — eager dynamic import is sufficient.
 */

import type { PostHog } from 'posthog-js'
import type { AnalyticsProvider } from '~/types/tracking'

const isDev = process.env.NODE_ENV === 'development'

interface PosthogConfig {
  publicKey: string
  apiHost: string
  uiHost: string
}

export const createPosthogProvider = (config: PosthogConfig): AnalyticsProvider => {
  const MAX_PENDING_ACTIONS = 100
  let posthogInstance: PostHog | null = null
  let isLoaded = false
  const pendingActions: Array<() => void> = []
  const { publicKey, apiHost, uiHost } = config

  const flushPendingActions = (): void => {
    if (!isLoaded || !posthogInstance) return

    while (pendingActions.length > 0) {
      const action = pendingActions.shift()
      action?.()
    }
  }

  async function loadPosthog(): Promise<void> {
    try {
      const { default: posthog } = await import('posthog-js')

      posthogInstance = posthog

      posthog.init(publicKey, {
        api_host: apiHost,
        ui_host: uiHost,
        capture_pageview: false,
        person_profiles: 'identified_only',
        loaded: () => {
          isLoaded = true
          flushPendingActions()

          if (isDev) {
            posthog.debug()
          }
        }
      })
    } catch {
      if (isDev) {
        console.warn('Failed to load PostHog')
      }
    }
  }

  return {
    name: 'posthog',

    init() {
      if (!publicKey) {
        if (isDev) {
          console.log('PostHog: disabled (no public key configured)')
        }
        return
      }

      loadPosthog()
    },

    identify(userId, properties) {
      if (isLoaded && posthogInstance) {
        posthogInstance.identify(userId, properties)
      } else if (publicKey && pendingActions.length < MAX_PENDING_ACTIONS) {
        pendingActions.push(() => {
          posthogInstance?.identify(userId, properties)
        })
      }
    },

    reset() {
      if (isLoaded && posthogInstance) {
        posthogInstance.reset()
      } else if (publicKey && pendingActions.length < MAX_PENDING_ACTIONS) {
        pendingActions.push(() => {
          posthogInstance?.reset()
        })
      }
    },

    track(event, properties) {
      if (isLoaded && posthogInstance) {
        posthogInstance.capture(event, properties)
      } else if (publicKey && pendingActions.length < MAX_PENDING_ACTIONS) {
        pendingActions.push(() => {
          posthogInstance?.capture(event, properties)
        })
      }
    },

    setUserProperties(properties) {
      if (isLoaded && posthogInstance) {
        posthogInstance.setPersonProperties(properties)
      } else if (publicKey && pendingActions.length < MAX_PENDING_ACTIONS) {
        pendingActions.push(() => {
          posthogInstance?.setPersonProperties(properties)
        })
      }
    },

    group(groupType, groupKey, properties) {
      if (isLoaded && posthogInstance) {
        posthogInstance.group(groupType, groupKey, properties)
      } else if (publicKey && pendingActions.length < MAX_PENDING_ACTIONS) {
        pendingActions.push(() => {
          posthogInstance?.group(groupType, groupKey, properties)
        })
      }
    },

    isAvailable() {
      return !!publicKey && isLoaded
    }
  }
}
